import React, { ComponentType, useEffect, useRef } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { LoaderGlobal } from '@bloomays-lib/ui.shared';
import { useAuthStore } from '@bloomays-lib/common.auth/browser';
import { Role } from '@bloomays-lib/types.shared';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

interface ProtectedRouteProps {
  Component: ComponentType;
  roles?: Role[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  Component,
  roles,
}: ProtectedRouteProps): React.JSX.Element => {
  const auth = useAuthStore((state) => state.auth);
  const authUser = auth?.user;
  const location = useLocation();
  const navigate = useNavigate();
  const hasRedirectedRef = useRef(false);

  // Vérifier s'il y a un token dans l'URL ou dans sessionStorage
  const hasTokenInUrl = new URLSearchParams(location.search).has('token');
  const hasTokenInStorage = sessionStorage.getItem('token') !== null;
  const hasToken = hasTokenInUrl || hasTokenInStorage;

  // Rediriger vers l'URL d'origine stockée une fois authentifié
  useEffect(() => {
    if (auth?.isAuthenticated && !hasRedirectedRef.current) {
      const originalPath = sessionStorage.getItem('originalPathAfterAuth');
      if (originalPath) {
        hasRedirectedRef.current = true;
        sessionStorage.removeItem('originalPathAfterAuth');
        void navigate(originalPath, { replace: true });
      }
    }
  }, [auth?.isAuthenticated, navigate]);

  // Attendre l'authentification si un token est présent
  if (hasToken && !auth?.isAuthenticated && auth?.isLoading !== false) {
    return <LoaderGlobal waitingText="Authentification en cours..." />;
  }

  if (!auth?.isAuthenticated) {
    return <Navigate to="/" />;
  }

  if (roles && auth?.isAuthenticated) {
    if (!roles.some((role) => authUser?.roles?.includes(role))) {
      return <Navigate to="/forbidden" />;
    } else {
      return <Component />;
    }
  }

  const ComponentAuthenticate = withAuthenticationRequired(Component, {
    onRedirecting: () => <LoaderGlobal waitingText="Redirection vers l'authentification" />,
  });
  return <ComponentAuthenticate />;
};

export default ProtectedRoute;
